/* eslint-disable no-console */
import http from '../lib/api';
import {
  UserSearchResponse,
  KnowledgeBaseShare,
  SharesResponse,
  KnowledgeBaseShareCreate,
  KnowledgeBaseShareUpdate,
} from '../schemas/knowledgeBase';

/**
 * Service for managing knowledge base sharing operations
 */
class KnowledgeBaseShareService {
  private readonly basePath = '/api/v1';

  /**
   * Search for users by username or email
   * @param query - Search query string
   * @param limit - Maximum number of results to return
   */
  async searchUsers(query: string, limit: number = 10): Promise<UserSearchResponse> {
    try {
      const response = await http.get(`${this.basePath}/auth/users/search`, {
        params: { q: query, limit },
      });
      return response.data;
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }

  /**
   * Get all shares for a knowledge base
   * @param knowledgeBaseId - The ID of the knowledge base
   */
  async getShares(knowledgeBaseId: string): Promise<SharesResponse> {
    try {
      const response = await http.get(`${this.basePath}/knowledge-bases/${knowledgeBaseId}/shares`);
      return response.data;
    } catch (error) {
      console.error('Error fetching shares:', error);
      throw error;
    }
  }

  /**
   * Create a new share for a knowledge base
   * @param knowledgeBaseId - The ID of the knowledge base
   * @param data - Share creation data
   */
  async createShare(
    knowledgeBaseId: string,
    data: KnowledgeBaseShareCreate
  ): Promise<KnowledgeBaseShare> {
    try {
      const response = await http.post(
        `${this.basePath}/knowledge-bases/${knowledgeBaseId}/shares`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error creating share:', error);
      throw error;
    }
  }

  /**
   * Update an existing share's permission
   * @param knowledgeBaseId - The ID of the knowledge base
   * @param shareId - The ID of the share to update
   * @param data - Share update data
   */
  async updateShare(
    knowledgeBaseId: string,
    shareId: number,
    data: KnowledgeBaseShareUpdate
  ): Promise<KnowledgeBaseShare> {
    try {
      const response = await http.put(
        `${this.basePath}/knowledge-bases/${knowledgeBaseId}/shares/${shareId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error updating share:', error);
      throw error;
    }
  }

  /**
   * Delete a share from a knowledge base
   * @param knowledgeBaseId - The ID of the knowledge base
   * @param shareId - The ID of the share to delete
   */
  async deleteShare(knowledgeBaseId: string, shareId: number): Promise<void> {
    try {
      await http.delete(`${this.basePath}/knowledge-bases/${knowledgeBaseId}/shares/${shareId}`);
    } catch (error) {
      console.error('Error deleting share:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const knowledgeBaseShareService = new KnowledgeBaseShareService();
